import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useSiteData } from "../context/SiteDataContext";
import { getSection, imageStyle, renderMultilineText, sectionStyle } from "../lib/sectionStyles";
import SafeImage from "../components/SafeImage";
import { getHomepagePortfolioItems } from "../lib/homepagePortfolio";

const accentBg = (a) =>
  a === "red"
    ? "bg-[var(--red)] text-[var(--pink-soft)]"
    : a === "pink"
    ? "bg-[var(--pink)] text-[var(--ink)]"
    : "bg-[var(--ink)] text-[var(--paper)]";

const ArticleGrid = () => {
  const { articles, siteSections } = useSiteData();
  const homepageArticles = getHomepagePortfolioItems(articles);
  const intro = getSection(siteSections, "home", "home.article-grid-intro", {
    eyebrow: "Section 02 - The Clippings",
    title: "STORIES,\nFILED.",
    body:
      "A working archive of cover features, interviews and culture pieces - cut from the page, pinned to the wall.",
    caption: "more in the archive",
  });

  if (!intro || homepageArticles.length === 0) return null;

  const titleLines = String(intro.title || "STORIES,\nFILED.").split(/\n+/).filter(Boolean);

  return (
    <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20" style={sectionStyle(intro)}>
      <div className="flex items-end justify-between gap-4 border-b-2 border-[var(--ink)] pb-4 mb-10">
        <div>
          <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-[var(--ink-soft)]">
            {intro.eyebrow}
          </div>
          <h2 className="font-masthead text-6xl md:text-8xl leading-[0.9] mt-2">
            <span className="relative inline-block">
              {titleLines[0] || "STORIES,"}
              <span
                className="absolute left-0 right-0 bottom-1 h-2 bg-[var(--pink)] -z-0"
                aria-hidden="true"
                style={{ zIndex: -1 }}
              />
            </span>
            <br />
            <span className="text-[var(--red)]">{titleLines[1] || "FILED."}</span>
          </h2>
        </div>
        <p className="hidden md:block max-w-md font-editorial italic text-lg text-[var(--ink-soft)]">
          {renderMultilineText(intro.body)}
        </p>
      </div>

      <div className="grid grid-cols-12 gap-5 md:gap-7">
        {homepageArticles.map((a, idx) => {
          const isFeature = idx % 7 === 0;
          const span = isFeature ? "md:col-span-6" : "md:col-span-4";
          const hasInternalPage = a.slug && (a.body || !a.externalUrl);
          const CardWrapper = ({ children }) =>
            hasInternalPage ? (
              <Link to={`/articles/${a.slug}`} className="block">
                {children}
              </Link>
            ) : a.externalUrl ? (
              <a href={a.externalUrl} target="_blank" rel="noopener noreferrer" className="block">
                {children}
              </a>
            ) : (
              <div>{children}</div>
            );

          return (
            <article
              key={a.id}
              className={`clipping col-span-12 sm:col-span-6 ${span} relative bg-[var(--paper)] border-2 border-[var(--ink)] shadow-[4px_4px_0_var(--ink)]`}
              style={{ transform: `rotate(${a.rotation}deg)` }}
            >
              <span className="tape -top-3 left-6 -rotate-6" />
              <CardWrapper>
                <div className="relative overflow-hidden bg-[var(--ink)]">
                  <SafeImage
                      containerClassName="w-full overflow-hidden"
                      src={a.image}
                      fallbackSrc="/og-image.png"
                      alt={a.imageAlt || a.title}
                      loading={idx < 2 ? "eager" : "lazy"}
                      width={isFeature ? "940" : "760"}
                      height={isFeature ? "564" : "950"}
                      className="w-full h-full object-cover transition-transform duration-700 hover:scale-[1.04]"
                      style={{ ...imageStyle(a), aspectRatio: isFeature ? "5 / 3" : "4 / 5" }}
                    />
                  <span
                    className={`absolute top-3 left-3 font-masthead tracking-[0.22em] text-[10px] px-2 py-1 ${accentBg(a.accent)}`}
                  >
                    {(a.category || "Story").toUpperCase()}
                  </span>
                  <span className="absolute bottom-3 right-3 font-mono text-[10px] tracking-[0.25em] bg-[var(--paper)] text-[var(--ink)] px-2 py-1">
                    {(a.publication || "Mai El Mokadem").toUpperCase()}
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="font-editorial text-2xl md:text-3xl leading-[1.1] tracking-tight">
                    {a.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-[var(--ink-soft)] line-clamp-3">
                    {a.excerpt}
                  </p>
                  <div className="mt-5 flex items-center justify-between">
                    <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--red)]">
                      {a.externalUrl && !a.body ? "Read externally" : "Read story"}
                    </span>
                    <ArrowUpRight size={18} className="text-[var(--ink)] group-hover:text-[var(--red)]" />
                  </div>
                </div>
              </CardWrapper>
            </article>
          );
        })}
      </div>

      <div className="mt-12 flex items-center justify-center">
        <span className="font-hand text-3xl text-[var(--red-deep)] -rotate-2">
          - {intro.caption || "more in the archive"} -
        </span>
      </div>
    </section>
  );
};

export default ArticleGrid;
